import type { StoredUiComponent } from "@/storage/uiComponentsRepository";
import type { StoredUiSidebarItem } from "@/storage/uiSidebarItemsRepository";
import type {
  UiPropertySpec,
  UiPropertySpecField,
} from "@/types/uiPropertySpec";

export type BuilderPropertyType =
  | "array"
  | "boolean"
  | "number"
  | "object"
  | "string";
export type BuilderPropertyValue =
  | unknown[]
  | boolean
  | number
  | Record<string, unknown>
  | string
  | null;

export interface BuilderProperty {
  color?: boolean;
  componentKind?: string;
  editable: boolean;
  fields?: UiPropertySpec;
  id: string;
  itemFields?: UiPropertySpec;
  label: string;
  multiline?: boolean;
  nullable: boolean;
  options?: readonly string[];
  path: readonly string[];
  type: BuilderPropertyType;
  value: BuilderPropertyValue;
  variants?: Readonly<Record<string, UiPropertySpec>>;
}

export interface BuilderPropertySource {
  props?: Record<string, unknown> | null;
  spec?: UiPropertySpec | null;
}

function createBuilderProperties(
  source: BuilderPropertySource,
  { editable = false }: { editable?: boolean } = {},
  basePath: readonly string[] = [],
  parentEditable = true,
): BuilderProperty[] {
  const props = normalizeBuilderProperties(source.props);
  const specEntries = Object.entries(source.spec ?? {});
  if (specEntries.length > 0) {
    return specEntries.map(([id, spec]) => (
      createPropertyFromSpec(id, props[id], spec, [...basePath, id], parentEditable)
    ));
  }

  const entries = Object.entries(props);

  return entries.map(([id, rawValue]) => ({
    editable,
    id,
    label: id,
    nullable: rawValue === null,
    path: [...basePath, id],
    type: inferBuilderPropertyType(rawValue),
    value: normalizeBuilderPropertyValue(rawValue),
  }));
}

export function createComponentBuilderProperties(
  component: StoredUiComponent,
): BuilderProperty[] {
  return createBuilderPropertiesFromRecordSource(
    createEntityPropertySourceFromRecord(component),
  );
}

export function createSidebarItemBuilderProperties(
  item: StoredUiSidebarItem,
): BuilderProperty[] {
  return createBuilderPropertiesFromRecordSource(item);
}

export function createRecordBuilderPropertiesFromRawObject(
  value: unknown,
): BuilderProperty[] {
  const record = isRecord(value) ? value : {};
  const props = readProps(record);

  return createBuilderProperties(
    {
      props: Object.keys(props).length > 0
        ? props
        : normalizeBuilderProperties(record),
    },
    { editable: false },
  );
}

export function createObjectBuilderProperties(
  property: BuilderProperty,
): BuilderProperty[] {
  if (property.type !== "object") return [];

  const props = isRecord(property.value) ? property.value : {};
  const objectKind = readObjectKind(props);
  const variantSpec = objectKind ? property.variants?.[objectKind] : undefined;
  const spec = variantSpec ?? property.fields;

  return createBuilderProperties(
    {
      props,
      ...(spec ? { spec } : {}),
    },
    { editable: property.editable },
    property.path,
    property.editable,
  ).filter((childProperty) => childProperty.id !== "kind");
}

export function createArrayItemBuilderProperties(
  property: BuilderProperty,
  index: number,
): BuilderProperty[] {
  const itemFields = property.itemFields;
  if (property.type !== "array" || !itemFields) return [];

  const items = readArrayPropertyItems(property);
  const item = items[index];
  if (item === undefined) return [];

  return createBuilderProperties(
    {
      props: normalizeArrayItemValue(item, itemFields),
      spec: itemFields,
    },
    { editable: property.editable },
    [...property.path, String(index)],
    property.editable,
  );
}

// Items are edited field by field, so a scalar item has to become a record
// first. It is written into the first field of the item spec, which is the one
// a scalar item stands for (a table column written as a bare string is its
// path).
export function normalizeArrayPropertyValue(
  property: BuilderProperty,
): BuilderPropertyValue {
  const itemFields = property.itemFields;
  if (property.type !== "array" || !itemFields) return property.value;

  return readArrayPropertyItems(property).map((item) => (
    normalizeArrayItemValue(item, itemFields)
  ));
}

function normalizeArrayItemValue(
  item: unknown,
  itemFields: UiPropertySpec,
): Record<string, unknown> {
  if (isRecord(item)) return item;
  if (item === null || item === undefined) return {};

  const [scalarFieldId] = Object.keys(itemFields);
  return scalarFieldId ? { [scalarFieldId]: item } : {};
}

function readArrayPropertyItems(property: BuilderProperty): unknown[] {
  return Array.isArray(property.value) ? property.value : [];
}

function createEntityPropertySourceFromRecord(
  source: BuilderPropertySource & {
    props?: Record<string, unknown> | null;
    uiScheme?: Record<string, unknown>;
  },
): BuilderPropertySource {
  return {
    props: readComponentProps(source),
    spec: source.spec,
  };
}

function createBuilderPropertiesFromRecordSource(
  source: BuilderPropertySource,
): BuilderProperty[] {
  return createBuilderProperties(source, { editable: false });
}

function createPropertyFromSpec(
  id: string,
  rawValue: unknown,
  spec: UiPropertySpecField,
  path: readonly string[],
  parentEditable: boolean,
): BuilderProperty {
  const readOnly = spec.read_only === true;
  const rawValueWithDefault = rawValue === undefined
    ? spec.default
    : rawValue;
  const fieldType = typeof spec.field_type === "string" ? spec.field_type : "";
  const label = typeof spec.display_name === "string" ? spec.display_name : id;
  const options = normalizeSpecOptions(spec.options);
  const fields = normalizePropertySpec(spec.fields);
  const itemFields = normalizePropertySpec(spec.items);
  const variants = normalizeSpecVariants(spec.variants);
  const type = normalizeSpecFieldType(
    fieldType,
    rawValueWithDefault,
    fields,
    variants,
  );
  const componentKind = typeof spec.component_kind === "string"
    ? spec.component_kind
    : undefined;
  const nullable = typeof spec.nullable === "boolean"
    ? spec.nullable
    : rawValueWithDefault === null;

  return {
    ...(isColorSpecFieldType(fieldType) ? { color: true } : {}),
    ...(componentKind ? { componentKind } : {}),
    editable: parentEditable && !readOnly,
    ...(fields ? { fields } : {}),
    id,
    ...(itemFields && type === "array" ? { itemFields } : {}),
    label,
    ...(isMultilineSpecFieldType(fieldType) ? { multiline: true } : {}),
    nullable,
    ...(options.length > 0 ? { options } : {}),
    path,
    type,
    value: normalizeSpecPropertyValue(rawValueWithDefault, type),
    ...(variants ? { variants } : {}),
  };
}

function normalizeSpecOptions(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];

  return value.filter((option): option is string => (
    typeof option === "string"
  ));
}

function normalizeSpecFieldType(
  fieldType: string,
  rawValue: unknown,
  fields: UiPropertySpec | undefined,
  variants: Readonly<Record<string, UiPropertySpec>> | undefined,
): BuilderPropertyType {
  if (fields || variants) return "object";

  switch (fieldType.trim().toLowerCase()) {
    case "array":
    case "list":
      return "array";
    case "boolean":
      return "boolean";
    case "dict":
    case "kind":
    case "map":
    case "object":
      return "object";
    case "integer":
    case "number":
      return "number";
    case "color":
    case "string":
    case "text":
      return "string";
    default:
      return inferBuilderPropertyType(rawValue);
  }
}

// `text` is a string that is expected to be long enough to need its own editing
// area rather than a single-line field.
function isMultilineSpecFieldType(fieldType: string): boolean {
  return fieldType.trim().toLowerCase() === "text";
}

// `color` is a string holding a CSS color, so it is edited both by hand and
// through a color picker.
function isColorSpecFieldType(fieldType: string): boolean {
  return fieldType.trim().toLowerCase() === "color";
}

function normalizeSpecPropertyValue(
  value: unknown,
  type: BuilderPropertyType,
): BuilderPropertyValue {
  if (value === undefined) return null;
  if (type === "boolean") {
    return typeof value === "boolean" ? value : null;
  }
  if (type === "number") {
    // `Number(null)` is 0, which would turn an unset nullable number into a real
    // zero and hide that the property has no value at all.
    if (value === null) return null;

    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  if (type === "object") {
    return isRecord(value) ? { ...value } : null;
  }
  if (type === "array") {
    return Array.isArray(value) ? cloneArrayValue(value) : [];
  }

  return normalizeBuilderPropertyValue(value);
}

function inferBuilderPropertyType(value: unknown): BuilderPropertyType {
  if (Array.isArray(value)) return "array";
  if (typeof value === "boolean") return "boolean";
  if (typeof value === "number" && Number.isFinite(value)) return "number";
  if (isRecord(value)) return "object";

  return "string";
}

function normalizeBuilderPropertyValue(value: unknown): BuilderPropertyValue {
  if (value === null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return cloneArrayValue(value);
  if (isRecord(value)) return { ...value };

  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

function cloneArrayValue(value: unknown[]): unknown[] {
  return value.map((item) => {
    if (Array.isArray(item)) return cloneArrayValue(item);
    if (isRecord(item)) return cloneRecordValue(item);
    return item;
  });
}

function cloneRecordValue(value: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(value).map(([key, item]) => {
      if (Array.isArray(item)) return [key, cloneArrayValue(item)];
      if (isRecord(item)) return [key, cloneRecordValue(item)];
      return [key, item];
    }),
  );
}

function normalizeBuilderProperties(
  value: unknown,
): Record<string, unknown> {
  if (!isRecord(value)) return {};

  return stripChildAndSystemKeys(value);
}

function readProps(value: { props?: unknown }): Record<string, unknown> {
  return normalizeBuilderProperties(value.props);
}

function readComponentProps(
  value: { props?: unknown; uiScheme?: unknown },
): Record<string, unknown> {
  const props = readProps(value);
  if (Object.keys(props).length > 0) return props;

  return normalizeBuilderProperties(value.uiScheme);
}

function stripChildAndSystemKeys(
  value: Record<string, unknown>,
): Record<string, unknown> {
  if (!isRecord(value)) return {};

  const {
    children,
    createdAt,
    updatedAt,
    uuid,
    storageKey,
    scopeKey,
    syncedAt,
    ...stripped
  } = value;

  return stripped;
}

function normalizePropertySpec(value: unknown): UiPropertySpec | undefined {
  if (!isRecord(value)) return undefined;

  const spec = Object.entries(value).reduce<UiPropertySpec>(
    (fields, [fieldId, fieldSpec]) => {
      if (isRecord(fieldSpec)) {
        fields[fieldId] = fieldSpec as UiPropertySpecField;
      }
      return fields;
    },
    {},
  );

  return Object.keys(spec).length > 0 ? spec : undefined;
}

function normalizeSpecVariants(
  value: unknown,
): Readonly<Record<string, UiPropertySpec>> | undefined {
  if (!isRecord(value)) return undefined;

  const variants = Object.entries(value).reduce<Record<string, UiPropertySpec>>(
    (normalizedVariants, [variantId, variantSpec]) => {
      const spec = normalizePropertySpec(variantSpec);
      if (spec) normalizedVariants[variantId] = spec;
      return normalizedVariants;
    },
    {},
  );

  return Object.keys(variants).length > 0 ? variants : undefined;
}

function readObjectKind(value: Record<string, unknown>): string | null {
  return typeof value.kind === "string" && value.kind.trim()
    ? value.kind
    : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
